import { http } from '../lib/api';
import { getSlot } from '../lib/accountSlot';
import type {
  AccountDto,
  McpConnectionDto,
  OAuthAuthorizeDecisionRequest,
  OAuthAuthorizeDecisionResponse,
  OAuthAuthorizeDetailsDto,
  OAuthAuthorizeQuery,
  GateMode,
  IntegrationScope,
  IntegrationTokenCreatedDto,
  IntegrationTokenDto,
  IntegrationWebhookDeliveryDto,
  IntegrationWebhookDto,
  ProjectAutonomy,
  AcceptInviteResponse,
  InvitePreviewDto,
  ActiveRunDto,
  ConstellationDto,
  HubHistoryDto,
  DashboardOverviewDto,
  DashboardProjectDto,
  DashboardPrototypeDto,
  ThreadDto,
  ThreadDetailDto,
  ThreadMessageDto,
  ThreadProposalDto,
  CloseIssueRequest,
  CreateFeedbackRequest,
  IssueAttachmentDto,
  IssueCommentDto,
  LogSearchHit,
  CreateIssueRequest,
  CreateLoopRequest,
  CreateProjectRequest,
  CreatePrototypeRequest,
  Disable2faRequest,
  Enable2faRequest,
  FireLoopResponse,
  GithubIssueDto,
  GithubRepoDto,
  InviteGrantInput,
  IssueBoardState,
  IssueDto,
  IssueTimelineEventDto,
  IterateRunRequest,
  PaginatedIssuesDto,
  LoginRequest,
  LoginResponse,
  LoginResult,
  LoginTotpRequest,
  LogLine,
  LoopDto,
  LoopHealthDto,
  MetricsDto,
  MyGrantDto,
  OrgSummary,
  WorkspaceEntitlementsDto,
  OrgInviteDto,
  OrgMemberDto,
  OrgMemberListDto,
  ProjectDto,
  ProjectFeedbackDto,
  ProjectGrantDto,
  ProjectRole,
  PrototypeCustomDomainDto,
  PrototypeDto,
  PrototypeInspectDto,
  PrototypeLogLine,
  PrototypePullDto,
  PrototypeSchemaDto,
  PrototypeApiSpecDto,
  PrototypeSecretDto,
  PrototypeAdminDto,
  PrototypeStatsDto,
  ProjectPullRequestDto,
  ProjectTransferDestinationDto,
  ProjectTransferDto,
  ProjectTransferResultDto,
  TransferProjectRequest,
  RecoveryCodesDto,
  RunDto,
  RunIssueRequest,
  RunSummaryDto,
  StageAttemptDto,
  StopAllRunsResponse,
  SetupRequest,
  SetupStatusDto,
  TwoFactorSetupDto,
  TwoFactorStatusDto,
  UpdateIssueRequest,
  UpdateLoopRequest,
  VersionDto,
  WorkspaceEntryDto,
  WorkspaceFileDto,
  WorkspacePrototypeDomainDto,
} from '@aiai/shared';

// ── Auth ────────────────────────────────────────────────────────────────────
// Slot 0 sends no `slot` field, so its request bodies stay byte-identical to
// before multi-account: an older API (forbidNonWhitelisted) still accepts them
// during a deploy.
function slotBody(): { slot?: number } {
  const slot = getSlot();
  return slot === 0 ? {} : { slot };
}

/** Step 1: password. May return { requiresTotp, mfaToken } or a full session. */
export async function login(payload: LoginRequest): Promise<LoginResult> {
  const { data } = await http.post<LoginResult>('/auth/login', { ...payload, ...slotBody() });
  return data;
}

/** Step 2 (when 2FA is enabled): exchange mfaToken + code for a session. */
export async function loginTotp(
  payload: LoginTotpRequest,
): Promise<LoginResponse> {
  const { data } = await http.post<LoginResponse>('/auth/login/totp', {
    ...payload,
    ...slotBody(),
  });
  return data;
}

/**
 * Exchange the HttpOnly refresh cookie for a new access token. Rejects (401)
 * when there's no valid refresh cookie — a normal outcome for a
 * never-logged-in visitor, not just an error case.
 */
export async function refreshSession(): Promise<LoginResponse> {
  const { data } = await http.post<LoginResponse>('/auth/refresh', undefined, {
    params: { u: getSlot() },
  });
  return data;
}

/** Clears refresh cookie(s) server-side: this tab's account, or every account. Best-effort. */
export async function logoutRequest(opts: { all?: boolean } = {}): Promise<void> {
  await http.post('/auth/logout', undefined, { params: opts.all ? { all: 1 } : { u: getSlot() } });
}

/** Accounts this browser is signed in to (identity only). With `workspace`, each says if it's a member. */
export async function fetchAccounts(
  workspace?: string,
  opts: { timeoutMs?: number } = {},
): Promise<AccountDto[]> {
  const { data } = await http.get<AccountDto[]>('/auth/accounts', {
    params: workspace ? { workspace } : undefined,
    timeout: opts.timeoutMs,
  });
  return data;
}

// ── Two-factor (TOTP) ────────────────────────────────────────────────────────
export async function fetch2faStatus(): Promise<TwoFactorStatusDto> {
  const { data } = await http.get<TwoFactorStatusDto>('/auth/2fa/status');
  return data;
}

export async function setup2fa(): Promise<TwoFactorSetupDto> {
  const { data } = await http.post<TwoFactorSetupDto>('/auth/2fa/setup');
  return data;
}

export async function enable2fa(
  payload: Enable2faRequest,
): Promise<RecoveryCodesDto> {
  const { data } = await http.post<RecoveryCodesDto>('/auth/2fa/enable', payload);
  return data;
}

export async function disable2fa(payload: Disable2faRequest): Promise<void> {
  await http.post('/auth/2fa/disable', payload);
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await http.post('/auth/change-password', { currentPassword, newPassword });
}

/** Permanently delete the caller's own account (password re-verified server-side). */
export async function deleteAccount(password: string): Promise<void> {
  await http.delete('/auth/account', { data: { password }, params: { u: getSlot() } });
}

// ── Orgs ──────────────────────────────────────────────────────────────────--
/** Orgs the caller is a member of — used to populate the org switcher and
 * resolve the active org on load. Not itself org-scoped (see OrgContext). */
export async function fetchOrgs(): Promise<OrgSummary[]> {
  const { data } = await http.get<OrgSummary[]>('/orgs');
  return data;
}

/** Owner-only rename of a workspace's display name and/or its URL slug. */
export async function updateOrg(
  orgId: string,
  payload: { name?: string; slug?: string },
): Promise<OrgSummary> {
  const { data } = await http.patch<OrgSummary>(`/orgs/${orgId}`, payload);
  return data;
}

/** This workspace's effective plan entitlements + live usage, for the plan/usage UI. */
export async function fetchWorkspaceEntitlements(
  orgId: string,
): Promise<WorkspaceEntitlementsDto> {
  const { data } = await http.get<WorkspaceEntitlementsDto>(
    `/orgs/${orgId}/entitlements`,
  );
  return data;
}

// ── Cross-workspace dashboard (/me) ─────────────────────────────────────────-
// Global endpoints that aggregate across every workspace the caller belongs to.
// Not workspace-scoped — the axios interceptor leaves `/me/*` un-prefixed.
export async function fetchMyOverview(): Promise<DashboardOverviewDto> {
  const { data } = await http.get<DashboardOverviewDto>('/me/overview');
  return data;
}

export async function fetchMyProjects(): Promise<DashboardProjectDto[]> {
  const { data } = await http.get<DashboardProjectDto[]>('/me/projects');
  return data;
}

export async function fetchMyPrototypes(): Promise<DashboardPrototypeDto[]> {
  const { data } = await http.get<DashboardPrototypeDto[]>('/me/prototypes');
  return data;
}

/** Everything notable that happened across the caller's visible planets since `since` (ISO); drives the replay. */
export async function fetchHubHistory(since: string): Promise<HubHistoryDto> {
  const { data } = await http.get<HubHistoryDto>('/me/hub/history', { params: { since } });
  return data;
}

/**
 * Prefix a path with `/w/<slug>` to target a specific workspace; unchanged when
 * no slug is given (the interceptor then uses the active workspace).
 */
function inWorkspace(path: string, slug?: string): string {
  return slug ? `/w/${encodeURIComponent(slug)}${path}` : path;
}

/** The caller's own per-project grants in the active (or given) workspace. */
export async function fetchMyGrants(workspaceSlug?: string): Promise<MyGrantDto[]> {
  const { data } = await http.get<MyGrantDto[]>(inWorkspace('/me/grants', workspaceSlug));
  return data;
}

// ── Projects ──────────────────────────────────────────────────────────────--
export async function fetchProjects(): Promise<ProjectDto[]> {
  const { data } = await http.get<ProjectDto[]>('/projects');
  return data;
}

export async function fetchProject(id: string): Promise<ProjectDto> {
  const { data } = await http.get<ProjectDto>(`/projects/${id}`);
  return data;
}

/** The open pull requests of a project (by code or uuid). */
export async function fetchProjectPullRequests(idOrCode: string): Promise<ProjectPullRequestDto[]> {
  const { data } = await http.get<ProjectPullRequestDto[]>(`/projects/${encodeURIComponent(idOrCode)}/pull-requests`);
  return data;
}

/** A project's shipped-issue stars (latest 36 + true total), by code or uuid. */
export async function fetchConstellation(idOrCode: string, workspaceSlug?: string): Promise<ConstellationDto> {
  const path = `/projects/${encodeURIComponent(idOrCode)}/constellation`;
  const { data } = await http.get<ConstellationDto>(inWorkspace(path, workspaceSlug));
  return data;
}

export async function createProject(
  payload: CreateProjectRequest,
): Promise<ProjectDto> {
  const { data } = await http.post<ProjectDto>('/projects', payload);
  return data;
}

export async function fetchProjectIssuesBoard(
  projectId: string,
  params: {
    status: IssueBoardState;
    tag?: string;
    q?: string;
    page: number;
    pageSize: number;
  },
): Promise<PaginatedIssuesDto> {
  const { data } = await http.get<PaginatedIssuesDto>(
    `/projects/${projectId}/issues/board`,
    {
      params: {
        status: params.status,
        ...(params.tag ? { tag: params.tag } : {}),
        ...(params.q ? { q: params.q } : {}),
        page: params.page,
        pageSize: params.pageSize,
      },
    },
  );
  return data;
}

export async function deleteProject(id: string): Promise<void> {
  await http.delete(`/projects/${id}`);
}

// ── Project transfer (projects and prototypes alike) ────────────────────────
/** The caller's other owned workspaces, each with its preflight result. */
export async function fetchTransferDestinations(
  projectId: string,
): Promise<ProjectTransferDestinationDto[]> {
  const { data } = await http.get<ProjectTransferDestinationDto[]>(
    `/projects/${projectId}/transfer/destinations`,
  );
  return data;
}

/** Moves now (caller owns the destination) or records a pending transfer. */
export async function transferProject(
  projectId: string,
  body: TransferProjectRequest,
): Promise<ProjectTransferResultDto> {
  const { data } = await http.post<ProjectTransferResultDto>(`/projects/${projectId}/transfer`, body);
  return data;
}

/** Pending transfers from the active workspace, waiting on the destination. */
export async function fetchOutgoingTransfers(): Promise<ProjectTransferDto[]> {
  const { data } = await http.get<ProjectTransferDto[]>('/transfers/outgoing');
  return data;
}

/** Source owner cancels a pending transfer (active workspace). */
export async function cancelTransfer(transferId: string): Promise<void> {
  await http.delete(`/transfers/${transferId}`);
}

// The incoming side spans every workspace the caller owns (the global
// Projects / Prototypes pages), so these name the workspace explicitly: the
// /w/<slug> prefix wins over the active workspace.
export async function fetchIncomingTransfers(orgSlug: string): Promise<ProjectTransferDto[]> {
  const { data } = await http.get<ProjectTransferDto[]>(`/w/${orgSlug}/transfers/incoming`);
  return data;
}

export async function acceptTransfer(
  orgSlug: string,
  transferId: string,
): Promise<ProjectTransferResultDto> {
  const { data } = await http.post<ProjectTransferResultDto>(
    `/w/${orgSlug}/transfers/${transferId}/accept`,
  );
  return data;
}

export async function declineTransfer(orgSlug: string, transferId: string): Promise<void> {
  await http.post(`/w/${orgSlug}/transfers/${transferId}/decline`);
}

export async function setProjectAutonomy(
  id: string,
  autonomy: ProjectAutonomy,
): Promise<ProjectDto> {
  const { data } = await http.patch<ProjectDto>(`/projects/${id}/autonomy`, {
    autonomy,
  });
  return data;
}

export async function setProjectGateMode(
  id: string,
  gateMode: GateMode,
): Promise<ProjectDto> {
  const { data } = await http.patch<ProjectDto>(`/projects/${id}/gate-mode`, {
    gateMode,
  });
  return data;
}

export async function fetchProjectFeedback(id: string): Promise<ProjectFeedbackDto> {
  const { data } = await http.get<ProjectFeedbackDto>(`/projects/${id}/feedback`);
  return data;
}

/** Enable (response carries the new token, once) or disable end-user feedback. */
export async function setProjectFeedback(
  id: string,
  enabled: boolean,
): Promise<ProjectFeedbackDto> {
  const { data } = await http.patch<ProjectFeedbackDto>(`/projects/${id}/feedback`, { enabled });
  return data;
}

/** Replace the token; the response carries the new one, once. */
export async function rotateProjectFeedback(id: string): Promise<ProjectFeedbackDto> {
  const { data } = await http.post<ProjectFeedbackDto>(`/projects/${id}/feedback/rotate`);
  return data;
}

export async function updateProject(
  id: string,
  dto: { name?: string; defaultBranch?: string },
): Promise<ProjectDto> {
  const { data } = await http.patch<ProjectDto>(`/projects/${id}`, dto);
  return data;
}

// ── Project grants (per-project RBAC, owner-only) ────────────────────────────
/** GET /projects/:id/grants — who holds a grant on this project. */
export async function fetchProjectGrants(projectId: string): Promise<ProjectGrantDto[]> {
  const { data } = await http.get<ProjectGrantDto[]>(`/projects/${projectId}/grants`);
  return data;
}

/** PUT /projects/:id/grants/:userId — set (create or update) a member's project role. */
export async function upsertProjectGrant(
  projectId: string,
  userId: string,
  role: ProjectRole,
): Promise<void> {
  await http.put(`/projects/${projectId}/grants/${userId}`, { role });
}

/** DELETE /projects/:id/grants/:userId — revoke a member's project role. */
export async function deleteProjectGrant(projectId: string, userId: string): Promise<void> {
  await http.delete(`/projects/${projectId}/grants/${userId}`);
}

// ── Prototypes ──────────────────────────────────────────────────────────────
export async function fetchPrototypes(): Promise<PrototypeDto[]> {
  const { data } = await http.get<PrototypeDto[]>('/prototypes');
  return data;
}

export async function fetchPrototype(id: string): Promise<PrototypeDto> {
  const { data } = await http.get<PrototypeDto>(`/prototypes/${id}`);
  return data;
}

export async function createPrototype(
  payload: CreatePrototypeRequest,
): Promise<PrototypeDto> {
  const { data } = await http.post<PrototypeDto>('/prototypes', payload);
  return data;
}

/**
 * Switch the prototype's design style. Files a proposed restyle issue (the
 * PM plans it; the autonomy dial governs release) and remembers the choice.
 */
export async function setPrototypeDesign(
  id: string,
  designId: string,
): Promise<PrototypeDto> {
  const { data } = await http.put<PrototypeDto>(`/prototypes/${id}/design`, { designId });
  return data;
}

export async function fetchPrototypePulls(
  id: string,
): Promise<PrototypePullDto[]> {
  const { data } = await http.get<PrototypePullDto[]>(
    `/prototypes/${id}/pulls`,
  );
  return data;
}

export async function fetchPrototypeStats(
  id: string,
): Promise<PrototypeStatsDto> {
  const { data } = await http.get<PrototypeStatsDto>(`/prototypes/${id}/stats`);
  return data;
}

export async function fetchPrototypeInspect(
  id: string,
): Promise<PrototypeInspectDto> {
  const { data } = await http.get<PrototypeInspectDto>(`/prototypes/${id}/inspect`);
  return data;
}

export async function fetchPrototypeLogs(
  id: string,
  tail = 200,
): Promise<PrototypeLogLine[]> {
  const { data } = await http.get<PrototypeLogLine[]>(`/prototypes/${id}/logs`, {
    params: { tail },
  });
  return data;
}

/** The last deploy's build output (kept after a failed deploy). */
export async function fetchPrototypeBuildLog(id: string): Promise<PrototypeLogLine[]> {
  const { data } = await http.get<PrototypeLogLine[]>(`/prototypes/${id}/build-log`);
  return data;
}

export async function fetchPrototypeSchema(
  id: string,
): Promise<PrototypeSchemaDto> {
  const { data } = await http.get<PrototypeSchemaDto>(`/prototypes/${id}/schema`);
  return data;
}

export async function fetchPrototypeApiSpec(id: string): Promise<PrototypeApiSpecDto> {
  const { data } = await http.get<PrototypeApiSpecDto>(`/prototypes/${id}/api-spec`);
  return data;
}

export async function mergePrototypePr(
  id: string,
  prNumber: number,
): Promise<void> {
  await http.post(`/prototypes/${id}/merge`, { prNumber });
}

export async function redeployPrototype(id: string): Promise<void> {
  await http.post(`/prototypes/${id}/redeploy`);
}

export async function stopPrototype(id: string): Promise<void> {
  await http.post(`/prototypes/${id}/stop`);
}

export async function startPrototype(id: string): Promise<void> {
  await http.post(`/prototypes/${id}/start`);
}

export async function restartPrototype(id: string): Promise<void> {
  await http.post(`/prototypes/${id}/restart`);
}

/** Wake a prototype idle sleep stopped. */
export async function wakePrototype(id: string): Promise<void> {
  await http.post(`/prototypes/${id}/wake`);
}

/** The "In production" switch: never put to sleep when idle. */
export async function setPrototypeProduction(id: string, inProduction: boolean): Promise<void> {
  await http.patch(`/prototypes/${id}/production`, { inProduction });
}

export async function deletePrototype(id: string): Promise<void> {
  await http.delete(`/prototypes/${id}`);
}

export async function fetchPrototypeSecrets(
  id: string,
): Promise<PrototypeSecretDto[]> {
  const { data } = await http.get<PrototypeSecretDto[]>(`/prototypes/${id}/secrets`);
  return data;
}

export async function setPrototypeSecret(
  id: string,
  key: string,
  value: string,
  visible?: boolean,
): Promise<PrototypeSecretDto> {
  const { data } = await http.put<PrototypeSecretDto>(
    `/prototypes/${id}/secrets/${encodeURIComponent(key)}`,
    visible === undefined ? { value } : { value, visible },
  );
  return data;
}

/** Visible → hidden only (revealing a hidden value needs a new value via setPrototypeSecret). */
export async function hidePrototypeSecret(id: string, key: string): Promise<PrototypeSecretDto> {
  const { data } = await http.patch<PrototypeSecretDto>(
    `/prototypes/${id}/secrets/${encodeURIComponent(key)}`,
    { visible: false },
  );
  return data;
}

/** A factory prototype's admin-account claim info; null for app-made / static prototypes. */
export async function fetchPrototypeAdminAccount(id: string): Promise<PrototypeAdminDto | null> {
  const { data } = await http.get<PrototypeAdminDto | ''>(`/prototypes/${id}/admin-account`);
  return data || null;
}

export async function deletePrototypeSecret(id: string, key: string): Promise<void> {
  await http.delete(`/prototypes/${id}/secrets/${encodeURIComponent(key)}`);
}

export async function addPrototypeDomain(
  id: string,
  domain: string,
): Promise<PrototypeCustomDomainDto> {
  const { data } = await http.post<PrototypeCustomDomainDto>(
    `/prototypes/${id}/domains`,
    { domain },
  );
  return data;
}

export async function verifyPrototypeDomain(
  id: string,
  domainId: string,
): Promise<PrototypeCustomDomainDto> {
  const { data } = await http.post<PrototypeCustomDomainDto>(
    `/prototypes/${id}/domains/${domainId}/verify`,
  );
  return data;
}

export async function deletePrototypeDomain(
  id: string,
  domainId: string,
): Promise<void> {
  await http.delete(`/prototypes/${id}/domains/${domainId}`);
}

// Workspace prototype domain (`*.app.customer.nl`) — owner-only, workspace-scoped.
export async function fetchWorkspaceDomain(): Promise<WorkspacePrototypeDomainDto> {
  const { data } = await http.get<WorkspacePrototypeDomainDto>('/prototype-domain');
  return data;
}

export async function putWorkspaceDomain(domain: string): Promise<WorkspacePrototypeDomainDto> {
  const { data } = await http.put<WorkspacePrototypeDomainDto>('/prototype-domain', { domain });
  return data;
}

export async function verifyWorkspaceDomain(): Promise<WorkspacePrototypeDomainDto> {
  const { data } = await http.post<WorkspacePrototypeDomainDto>('/prototype-domain/verify');
  return data;
}

/** Relabel every deployed prototype onto the active workspace domain again. */
export async function reapplyWorkspaceDomain(): Promise<{ queued: number }> {
  const { data } = await http.post<{ queued: number }>('/prototype-domain/reapply');
  return data;
}

export async function deleteWorkspaceDomain(): Promise<void> {
  await http.delete('/prototype-domain');
}

export async function setPrototypeStorage(
  id: string,
  enabled: boolean,
): Promise<PrototypeDto> {
  const { data } = await http.put<PrototypeDto>(`/prototypes/${id}/storage`, {
    enabled,
  });
  return data;
}

export async function setPrototypeFeedback(
  id: string,
  enabled: boolean,
): Promise<PrototypeDto> {
  const { data } = await http.put<PrototypeDto>(`/prototypes/${id}/feedback`, {
    enabled,
  });
  return data;
}

export async function rotatePrototypeFeedback(id: string): Promise<PrototypeDto> {
  const { data } = await http.post<PrototypeDto>(`/prototypes/${id}/feedback/rotate`);
  return data;
}

export async function setPrototypeResourceLimits(
  id: string,
  limits: { memoryMb: number | null; cpuLimit: number | null },
): Promise<PrototypeDto> {
  const { data } = await http.put<PrototypeDto>(`/prototypes/${id}/resources`, limits);
  return data;
}

// ── GitHub Issues (ticket backend) ──────────────────────────────────────────
export async function fetchGithubIssues(
  projectId: string,
  params: { state?: 'open' | 'closed' | 'all'; labels?: string } = {},
): Promise<GithubIssueDto[]> {
  const { data } = await http.get<GithubIssueDto[]>(
    `/projects/${projectId}/github/issues`,
    { params },
  );
  return data;
}

/** Pull a repo issue into the loop (creates/refreshes a local mirror). */
export async function pullInGithubIssue(
  projectId: string,
  number: number,
): Promise<IssueDto> {
  const { data } = await http.post<IssueDto>(
    `/projects/${projectId}/github/issues/${number}/pull-in`,
  );
  return data;
}

// ── Issues ──────────────────────────────────────────────────────────────────
export async function fetchIssue(id: string): Promise<IssueDto> {
  const { data } = await http.get<IssueDto>(`/issues/${id}`);
  return data;
}

export async function createIssue(
  payload: CreateIssueRequest,
  workspaceSlug?: string,
): Promise<IssueDto> {
  const { data } = await http.post<IssueDto>(inWorkspace('/issues', workspaceSlug), payload);
  return data;
}

export async function updateIssue(
  id: string,
  payload: UpdateIssueRequest,
  workspaceSlug?: string,
): Promise<IssueDto> {
  const { data } = await http.patch<IssueDto>(inWorkspace(`/issues/${id}`, workspaceSlug), payload);
  return data;
}

export async function deleteIssue(id: string): Promise<void> {
  await http.delete(`/issues/${id}`);
}

export async function closeIssue(
  id: string,
  payload: CloseIssueRequest,
  workspaceSlug?: string,
): Promise<IssueDto> {
  const { data } = await http.post<IssueDto>(inWorkspace(`/issues/${id}/close`, workspaceSlug), payload);
  return data;
}

export interface BulkIssueItemResult {
  id: string;
  ok: boolean;
  skipped: boolean;
  error?: string;
}

export async function bulkIssueAction(payload: {
  ids: string[];
  action: 'complete' | 'close' | 'delete' | 'run';
}): Promise<BulkIssueItemResult[]> {
  const { data } = await http.post<BulkIssueItemResult[]>('/issues/bulk', payload);
  return data;
}

export async function runIssue(id: string, body?: RunIssueRequest, workspaceSlug?: string): Promise<RunDto> {
  const { data } = await http.post<RunDto>(inWorkspace(`/issues/${id}/run`, workspaceSlug), body);
  return data;
}

export async function fetchIssueRuns(id: string): Promise<RunSummaryDto[]> {
  const { data } = await http.get<RunSummaryDto[]>(`/issues/${id}/runs`);
  return data;
}

/** Governance feed: who did what to this issue and why, in order. */
export async function fetchIssueTimeline(
  id: string,
): Promise<IssueTimelineEventDto[]> {
  const { data } = await http.get<IssueTimelineEventDto[]>(
    `/issues/${id}/timeline`,
  );
  return data;
}

export async function fetchIssueComments(id: string): Promise<IssueCommentDto[]> {
  const { data } = await http.get<IssueCommentDto[]>(`/issues/${id}/comments`);
  return data;
}

export async function addIssueComment(
  id: string,
  body: string,
): Promise<IssueCommentDto> {
  const { data } = await http.post<IssueCommentDto>(`/issues/${id}/comments`, { body });
  return data;
}

export async function deleteIssueComment(
  issueId: string,
  commentId: string,
): Promise<void> {
  await http.delete(`/issues/${issueId}/comments/${commentId}`);
}

export async function fetchIssueAttachments(issueId: string): Promise<IssueAttachmentDto[]> {
  const { data } = await http.get<IssueAttachmentDto[]>(`/issues/${issueId}/attachments`);
  return data;
}

export async function uploadIssueAttachment(
  issueId: string,
  file: File,
): Promise<IssueAttachmentDto> {
  const form = new FormData();
  form.append('file', file);
  // The shared axios instance defaults Content-Type to application/json; for a
  // FormData body axios v1 would then serialize it TO JSON (losing the file).
  // Force multipart so axios leaves the FormData intact and the browser sets
  // the boundary — otherwise the API receives no file ("No file provided").
  const { data } = await http.post<IssueAttachmentDto>(
    `/issues/${issueId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function deleteIssueAttachment(
  issueId: string,
  attachmentId: string,
): Promise<void> {
  await http.delete(`/issues/${issueId}/attachments/${attachmentId}`);
}

// ── Feedback ──────────────────────────────────────────────────────────────--
export async function createFeedback(
  payload: CreateFeedbackRequest,
): Promise<{ ok: true }> {
  const { data } = await http.post<{ ok: true }>('/feedback', payload);
  return data;
}

// ── Threads (project discussions with the team + PM) ─────────────────────────
export type ThreadListStatus = 'open' | 'resolved' | 'all';

/** The project's threads, most recent activity first. */
export async function fetchThreads(
  projectId: string,
  status: ThreadListStatus = 'open',
): Promise<ThreadDto[]> {
  const { data } = await http.get<ThreadDto[]>(`/projects/${projectId}/threads`, {
    params: { status },
  });
  return data;
}

/** "New thread": creates the thread together with its opening post. */
export async function createThread(
  projectId: string,
  input: { title?: string; body: string },
  workspaceSlug?: string,
): Promise<{ thread: ThreadDetailDto; message: ThreadMessageDto }> {
  const { data } = await http.post<{
    thread: ThreadDetailDto;
    message: ThreadMessageDto;
  }>(inWorkspace(`/projects/${projectId}/threads`, workspaceSlug), input);
  return data;
}

/** One thread with its discovered-issues ledger. */
export async function fetchThread(projectId: string, threadId: string): Promise<ThreadDetailDto> {
  const { data } = await http.get<ThreadDetailDto>(`/projects/${projectId}/threads/${threadId}`);
  return data;
}

/** Rename and/or resolve/reopen a thread. */
export async function updateThread(
  projectId: string,
  threadId: string,
  patch: { title?: string; resolved?: boolean },
): Promise<ThreadDetailDto> {
  const { data } = await http.patch<ThreadDetailDto>(`/projects/${projectId}/threads/${threadId}`, patch);
  return data;
}

/** Delete a thread, its posts and its ledger (filed issues stay). */
export async function deleteThread(projectId: string, threadId: string): Promise<void> {
  await http.delete(`/projects/${projectId}/threads/${threadId}`);
}

/**
 * One thread's posts, newest-first. `before` (an ISO createdAt cursor) pages
 * backwards into history. `limit` names the page size explicitly (server
 * clamps to 1–100, default 50) so the caller's "full page ⇒ more history"
 * check never silently drifts from the server's default.
 */
export async function fetchThreadMessages(
  projectId: string,
  threadId: string,
  before?: string,
  limit?: number,
): Promise<ThreadMessageDto[]> {
  const params: Record<string, string | number> = {};
  if (before) params.before = before;
  if (limit != null) params.limit = limit;
  const { data } = await http.get<ThreadMessageDto[]>(
    `/projects/${projectId}/threads/${threadId}/messages`,
    { params: Object.keys(params).length > 0 ? params : undefined },
  );
  return data;
}

export async function postThreadMessage(
  projectId: string,
  threadId: string,
  body: string,
): Promise<ThreadMessageDto> {
  const { data } = await http.post<ThreadMessageDto>(
    `/projects/${projectId}/threads/${threadId}/messages`,
    { body },
  );
  return data;
}

/** File a suggested proposal as an issue (`build` releases it to the Engineer straight away). */
export async function fileThreadProposal(
  projectId: string,
  threadId: string,
  proposalId: string,
  opts: { build?: boolean } = {},
): Promise<ThreadProposalDto> {
  const { data } = await http.post<ThreadProposalDto>(
    `/projects/${projectId}/threads/${threadId}/proposals/${proposalId}/file`,
    { build: opts.build ?? false },
  );
  return data;
}

export async function dismissThreadProposal(
  projectId: string,
  threadId: string,
  proposalId: string,
): Promise<ThreadProposalDto> {
  const { data } = await http.post<ThreadProposalDto>(
    `/projects/${projectId}/threads/${threadId}/proposals/${proposalId}/dismiss`,
  );
  return data;
}

export async function restoreThreadProposal(
  projectId: string,
  threadId: string,
  proposalId: string,
): Promise<ThreadProposalDto> {
  const { data } = await http.post<ThreadProposalDto>(
    `/projects/${projectId}/threads/${threadId}/proposals/${proposalId}/restore`,
  );
  return data;
}

/** Link a proposal to an issue the member created by hand ("Edit first"). */
export async function linkThreadProposal(
  projectId: string,
  threadId: string,
  proposalId: string,
  issueId: string,
): Promise<ThreadProposalDto> {
  const { data } = await http.patch<ThreadProposalDto>(
    `/projects/${projectId}/threads/${threadId}/proposals/${proposalId}`,
    { issueId },
  );
  return data;
}

/**
 * The in-flight PM thread turn (chat-lane run) for this project, if any.
 * Derived from the shared active-runs endpoint (no dedicated route) so a
 * thread page can seed its "PM is thinking" state on mount before the socket
 * delivers its first event.
 */
export async function fetchActiveThreadRun(
  projectId: string,
): Promise<ActiveRunDto | null> {
  const runs = await fetchActiveRuns();
  return (
    runs.find((r) => r.projectId === projectId && r.lane === 'chat') ?? null
  );
}

// ── Runs ──────────────────────────────────────────────────────────────────--
export async function fetchRun(id: string): Promise<RunDto> {
  const { data } = await http.get<RunDto>(`/runs/${id}`);
  return data;
}

export async function cancelRun(id: string): Promise<RunDto> {
  const { data } = await http.post<RunDto>(`/runs/${id}/cancel`);
  return data;
}

/** Re-check this run's pull request against GitHub right now. */
export async function refreshRunPullRequest(id: string): Promise<RunDto> {
  const { data } = await http.post<RunDto>(`/runs/${id}/refresh-pr`);
  return data;
}

/** Merge this run's held PR from the app — no GitHub round-trip needed. */
export async function mergeRunPullRequest(id: string): Promise<RunDto> {
  const { data } = await http.post<RunDto>(`/runs/${id}/merge`);
  return data;
}

/** Update this run's PR branch with the latest base branch commits (resolves "behind" state). */
export async function updateRunPullRequestBranch(id: string): Promise<RunDto> {
  const { data } = await http.post<RunDto>(`/runs/${id}/update-branch`);
  return data;
}

/** Continue a finished run's branch with feedback (creates a child run). */
export async function iterateRun(
  id: string,
  payload: IterateRunRequest,
): Promise<RunDto> {
  const { data } = await http.post<RunDto>(`/runs/${id}/iterate`, payload);
  return data;
}

export async function fetchActiveRuns(): Promise<ActiveRunDto[]> {
  const { data } = await http.get<ActiveRunDto[]>('/runs/active');
  return data;
}

export async function stopAllRuns(): Promise<StopAllRunsResponse> {
  const { data } = await http.post<StopAllRunsResponse>('/runs/stop-all');
  return data;
}

export async function fetchRunLogs(id: string): Promise<LogLine[]> {
  const { data } = await http.get<LogLine[]>(`/runs/${id}/logs`);
  return data;
}

export async function fetchRunAttempts(id: string): Promise<StageAttemptDto[]> {
  const { data } = await http.get<StageAttemptDto[]>(`/runs/${id}/attempts`);
  return data;
}

/** Search log text across all runs visible to the caller. */
export async function searchRunLogs(q: string, limit = 50): Promise<LogSearchHit[]> {
  const { data } = await http.get<LogSearchHit[]>('/runs/search/logs', {
    params: { q, limit },
  });
  return data;
}

/** List files in the run's cloned workspace (repo-relative paths). */
export async function listWorkspaceFiles(
  id: string,
): Promise<WorkspaceEntryDto[]> {
  const { data } = await http.get<WorkspaceEntryDto[]>(`/runs/${id}/workspace`);
  return data;
}

/** Fetch a single file from the run's workspace (current content). */
export async function getWorkspaceFile(
  id: string,
  filePath: string,
): Promise<WorkspaceFileDto> {
  const { data } = await http.get<WorkspaceFileDto>(
    `/runs/${id}/workspace/file`,
    { params: { path: filePath } },
  );
  return data;
}

/** Fetch the BASE (pre-run) version of a file — the "before" side of a diff.
 *  Empty content means the file was newly added during this run. */
export async function getWorkspaceBaseFile(
  id: string,
  filePath: string,
): Promise<WorkspaceFileDto> {
  const { data } = await http.get<WorkspaceFileDto>(
    `/runs/${id}/workspace/base`,
    { params: { path: filePath } },
  );
  return data;
}

/** The deployed build identity (v1.<n>), shown as a badge in the header. */
export async function fetchVersion(): Promise<VersionDto> {
  const { data } = await http.get<VersionDto>('/version');
  return data;
}

// ── Metrics ─────────────────────────────────────────────────────────────────
/**
 * A metrics time range: a trailing window in days, all time, or an explicit
 * from/to pair of YYYY-MM-DD dates (to inclusive).
 */
export type MetricsRange = { days: number } | { all: true } | { from: string; to: string };

/** Run/cost metrics for the active workspace (scoped server-side by X-Org-Id). */
export async function fetchMetrics(range: MetricsRange = { days: 30 }): Promise<MetricsDto> {
  const params =
    'all' in range ? { days: 'all' } : 'from' in range ? { from: range.from, to: range.to } : { days: range.days };
  const { data } = await http.get<MetricsDto>('/metrics', { params });
  return data;
}

export async function recordScreenVisit(screen: string): Promise<void> {
  await http.post('/metrics/screen-visit', { screen });
}

export async function recordInteraction(element: string, screen: string): Promise<void> {
  await http.post('/metrics/interaction', { element, screen });
}

// ── GitHub ──────────────────────────────────────────────────────────────────
export async function fetchGithubRepos(): Promise<GithubRepoDto[]> {
  const { data } = await http.get<GithubRepoDto[]>('/github/repos');
  return data;
}

// ── Settings ──────────────────────────────────────────────────────────────--
/**
 * Settings status. Secrets are presence-only booleans; non-secret operational
 * config (e.g. AGENT_REGISTRY_USER) returns its effective string value.
 */
export type SettingsStatus = Record<string, boolean | string>;

export async function fetchSettingsStatus(): Promise<SettingsStatus> {
  const { data } = await http.get<SettingsStatus>('/settings');
  return data;
}

export async function putSetting(key: string, value: string): Promise<void> {
  await http.put(`/settings/${key}`, { value });
}

export async function deleteSetting(key: string): Promise<void> {
  await http.delete(`/settings/${key}`);
}

export async function testTelegramConnection(): Promise<{ ok: true }> {
  const { data } = await http.post<{ ok: true }>('/settings/telegram/test');
  return data;
}

// ── MCP connections (OAuth: connected apps + the browser consent page) ──
// Account-level, not workspace-scoped: a connection reaches every workspace
// its holder belongs to (see GLOBAL_API_PREFIXES `/oauth`).

export async function getOAuthAuthorizeDetails(query: OAuthAuthorizeQuery): Promise<OAuthAuthorizeDetailsDto> {
  const { data } = await http.get<OAuthAuthorizeDetailsDto>('/oauth/authorize/details', { params: query });
  return data;
}

export async function decideOAuthAuthorize(
  body: OAuthAuthorizeDecisionRequest,
): Promise<OAuthAuthorizeDecisionResponse> {
  const { data } = await http.post<OAuthAuthorizeDecisionResponse>('/oauth/authorize', body);
  return data;
}

export async function listMcpConnections(): Promise<McpConnectionDto[]> {
  const { data } = await http.get<McpConnectionDto[]>('/oauth/connections');
  return data;
}

export async function revokeMcpConnection(id: string): Promise<void> {
  await http.delete(`/oauth/connections/${id}`);
}

// ── Integration tokens (owner-only; external sites → /api/integrations/v1) ──
// `projectId` = that project's tokens (Team page); `null` = the workspace's
// prototype-factory tokens (Workspace settings).

function integrationTokensPath(projectId: string | null): string {
  return projectId === null ? '/integration-tokens' : `/projects/${projectId}/integration-tokens`;
}

export async function listIntegrationTokens(projectId: string | null): Promise<IntegrationTokenDto[]> {
  const { data } = await http.get<IntegrationTokenDto[]>(integrationTokensPath(projectId));
  return data;
}

/** The plaintext `token` is present ONLY in this response — never fetched again. */
export async function createIntegrationToken(
  projectId: string | null,
  name: string,
  scopes?: IntegrationScope[],
): Promise<IntegrationTokenCreatedDto> {
  const { data } = await http.post<IntegrationTokenCreatedDto>(integrationTokensPath(projectId), {
    name,
    ...(scopes ? { scopes } : {}),
  });
  return data;
}

export async function revokeIntegrationToken(projectId: string | null, tokenId: string): Promise<void> {
  await http.delete(`${integrationTokensPath(projectId)}/${tokenId}`);
}

/** Set a token's status-change webhook. `secret` is set ONLY when one was just generated. */
export async function setIntegrationWebhook(
  projectId: string | null,
  tokenId: string,
  url: string,
): Promise<IntegrationWebhookDto> {
  const { data } = await http.put<IntegrationWebhookDto>(
    `${integrationTokensPath(projectId)}/${tokenId}/webhook`,
    { url },
  );
  return data;
}

/** Replace the webhook signing secret — the new one is in this response only. */
export async function rotateIntegrationWebhookSecret(
  projectId: string | null,
  tokenId: string,
): Promise<IntegrationWebhookDto> {
  const { data } = await http.post<IntegrationWebhookDto>(
    `${integrationTokensPath(projectId)}/${tokenId}/webhook/rotate-secret`,
  );
  return data;
}

export async function clearIntegrationWebhook(projectId: string | null, tokenId: string): Promise<void> {
  await http.delete(`${integrationTokensPath(projectId)}/${tokenId}/webhook`);
}

export async function listIntegrationWebhookDeliveries(
  projectId: string | null,
  tokenId: string,
): Promise<IntegrationWebhookDeliveryDto[]> {
  const { data } = await http.get<IntegrationWebhookDeliveryDto[]>(
    `${integrationTokensPath(projectId)}/${tokenId}/webhook/deliveries`,
  );
  return data;
}

// ── Scheduled Loops ─────────────────────────────────────────────────────────
export async function fetchProjectLoops(projectId: string): Promise<LoopDto[]> {
  const { data } = await http.get<LoopDto[]>(`/projects/${projectId}/loops`);
  return data;
}

export async function fetchLoop(id: string): Promise<LoopDto> {
  const { data } = await http.get<LoopDto>(`/loops/${id}`);
  return data;
}

export async function createLoop(payload: CreateLoopRequest): Promise<LoopDto> {
  const { data } = await http.post<LoopDto>('/loops', payload);
  return data;
}

export async function updateLoop(
  id: string,
  payload: UpdateLoopRequest,
): Promise<LoopDto> {
  const { data } = await http.patch<LoopDto>(`/loops/${id}`, payload);
  return data;
}

export async function deleteLoop(id: string): Promise<void> {
  await http.delete(`/loops/${id}`);
}

export async function fireLoop(
  id: string,
): Promise<FireLoopResponse | { skipped: true }> {
  const { data } = await http.post<FireLoopResponse | { skipped: true }>(
    `/loops/${id}/fire`,
  );
  return data;
}

export async function fetchLoopHealth(id: string): Promise<LoopHealthDto> {
  const { data } = await http.get<LoopHealthDto>(`/loops/${id}/health`);
  return data;
}

// ── First-run setup ─────────────────────────────────────────────────────────
export async function fetchSetupStatus(): Promise<SetupStatusDto> {
  const { data } = await http.get<SetupStatusDto>('/setup/status');
  return data;
}

export async function completeSetup(
  payload: SetupRequest,
): Promise<LoginResponse> {
  const { data } = await http.post<LoginResponse>('/setup', payload);
  return data;
}

export async function uploadImage(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  // Force multipart: the shared axios instance defaults Content-Type to
  // application/json, and axios v1 serializes a FormData body TO JSON when the
  // content-type is json — dropping the file. Setting multipart here makes
  // axios keep the FormData and let the browser add the boundary.
  const { data } = await http.post<{ url: string }>('/uploads', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ── Org member management ────────────────────────────────────────────────────

export { OrgInviteDto, OrgMemberListDto };

export async function fetchOrgMembers(orgId: string): Promise<OrgMemberListDto> {
  const { data } = await http.get<OrgMemberListDto>(`/orgs/${orgId}/members`);
  return data;
}

/** `grants` materialize into project grants for the invitee once they accept. */
export async function inviteOrgMember(
  orgId: string,
  email: string,
  role: 'owner' | 'member' = 'member',
  grants: InviteGrantInput[] = [],
): Promise<OrgInviteDto & { token?: string }> {
  const { data } = await http.post<OrgInviteDto & { token?: string }>(
    `/orgs/${orgId}/members/invite`,
    { email, role, ...(grants.length > 0 ? { grants } : {}) },
  );
  return data;
}

export async function removeOrgMember(orgId: string, userId: string): Promise<void> {
  await http.delete(`/orgs/${orgId}/members/${userId}`);
}

/** Promote a member to owner or demote an owner to member (owner only; never the last owner). */
export async function changeOrgMemberRole(
  orgId: string,
  userId: string,
  role: 'owner' | 'member',
): Promise<OrgMemberDto> {
  const { data } = await http.patch<OrgMemberDto>(`/orgs/${orgId}/members/${userId}`, { role });
  return data;
}

/** Leave a workspace you belong to (any member; an owner only while another owner remains). */
export async function leaveOrg(orgId: string): Promise<void> {
  await http.post(`/orgs/${orgId}/leave`);
}

export async function revokeOrgInvite(orgId: string, inviteId: string): Promise<void> {
  await http.delete(`/orgs/${orgId}/invitations/${inviteId}`);
}

export async function resendOrgInvite(
  orgId: string,
  inviteId: string,
): Promise<OrgInviteDto & { token?: string }> {
  const { data } = await http.post<OrgInviteDto & { token?: string }>(
    `/orgs/${orgId}/invitations/${inviteId}/resend`,
  );
  return data;
}

export async function acceptOrgInvite(token: string): Promise<AcceptInviteResponse> {
  const { data } = await http.post<AcceptInviteResponse>('/invitations/accept', { token });
  return data;
}

export async function previewOrgInvite(token: string): Promise<InvitePreviewDto> {
  const { data } = await http.get<InvitePreviewDto>(`/invitations/preview?token=${encodeURIComponent(token)}`);
  return data;
}

export async function registerFromInvite(
  token: string,
  password: string,
): Promise<LoginResponse> {
  const { data } = await http.post<LoginResponse>(
    '/invitations/register',
    { token, password },
    { params: { u: getSlot() } },
  );
  return data;
}


